import { Action } from '@ngrx/store';

/**
 * Metadata for instance-scoped actions.
 * Actions with this metadata will be routed to the correct store slice
 * based on the instanceId.
 */
export interface InstanceActionMeta {
    /**
     * The configurator instance ID this action targets
     */
    instanceId: string;
}

/**
 * Base interface for instance-scoped actions.
 * All actions that need to target a specific configurator instance
 * should implement this interface.
 */
export interface InstanceAction extends Action {
    meta?: InstanceActionMeta;
}

/**
 * Helper to wrap any action with instance scope.
 * Use this when dispatching actions from services that are instance-aware.
 *
 * @param action The original action
 * @param instanceId The target instance ID
 * @returns A new action with instance metadata
 *
 * @example
 * ```typescript
 * // In a service
 * setShutterType(shutterType: ShutterType) {
 *     const action = new SetShutterType({ shutterType });
 *     if (this.context) {
 *         this.store.dispatch(withInstanceScope(action, this.context.instanceId));
 *     } else {
 *         this.store.dispatch(action);
 *     }
 * }
 * ```
 */
export function withInstanceScope<T extends Action>(
    action: T,
    instanceId: string
): T & InstanceAction {
    return {
        ...action,
        meta: { instanceId },
    };
}

/**
 * Check if an action is instance-scoped
 */
export function isInstanceAction(action: Action): action is InstanceAction {
    return 'meta' in action && (action as InstanceAction).meta?.instanceId !== undefined;
}

/**
 * Get instance ID from an action, or null if not instance-scoped
 */
export function getInstanceId(action: Action): string | null {
    if (isInstanceAction(action)) {
        return action.meta?.instanceId ?? null;
    }
    return null;
}
